import { createRouter, createWebHistory } from 'vue-router';

import { isAuthenticated, hasOrganisation, isSuperAdmin, hydrateUser, hydrateOrganisation } from '@/kernel/session';
import { getCollection, getCollections } from '@/kernel/collections';

import LoginPage from '@/components/pages/login-page.vue';
import HomePage from '@/components/pages/home-page.vue';
import ContentLibraryPage from '@/components/pages/content-library-page.vue';
import NotFoundPage from '@/components/pages/not-found-page.vue';

import HistoricalPortfolioReportPage from '@/components/reports/pages/historical-portfolio-report-page.vue';
import AnnualPortfolioReportPage from '@/components/reports/pages/annual-portfolio-report-page.vue';
import HistoricalAssetReportPage from '@/components/reports/pages/historical-asset-report-page.vue';
import AnnualAssetReportPage from '@/components/reports/pages/annual-asset-report-page.vue';
import HistoricalAssetManagerReportPage from '@/components/reports/pages/historical-asset-manager-report-page.vue';
import AnnualAssetManagerReportPage from '@/components/reports/pages/annual-asset-manager-report-page.vue';
import PlatformGrowthReportPage from '@/components/reports/pages/platform-growth-report-page.vue';
import OrganisationsUsersReportPage from '@/components/reports/pages/organisations-users-report-page.vue';

import HistoricalPortfolioExportPage from '@/components/reports/exports/historical-portfolio-export-page.vue';
import AnnualPortfolioExportPage from '@/components/reports/exports/annual-portfolio-export-page.vue';
import HistoricalAssetExportPage from '@/components/reports/exports/historical-asset-export-page.vue';
import AnnualAssetExportPage from '@/components/reports/exports/annual-asset-export-page.vue';
import HistoricalAssetManagerExportPage from '@/components/reports/exports/historical-asset-manager-export-page.vue';
import AnnualAssetManagerExportPage from '@/components/reports/exports/annual-asset-manager-export-page.vue';

import CollectionIndexPage from '@/components/pages/collection-index-page.vue';
import CollectionCreatePage from '@/components/pages/collection-create-page.vue';
import CollectionEditPage from '@/components/pages/collection-edit-page.vue';

import AssessmentInterviewPage from '@/components/pages/assessment-interview-page.vue';
import AssessmentTemplateExportPage from '@/components/pages/assessment-template-export-page.vue';

import ImportPage from '@/components/pages/import-page.vue';

const collections = getCollections();

const collectionRoutes = collections.flatMap((collection) => {
  const key = collection.getKey();

  if (!collection.getAutoRoute()) {
    return null;
  }

  return [
    {
      path: collection.getIndexUrl(),
      name: `index-${key}`,
      component: collection.getIndexPageComponent() || CollectionIndexPage,
      props: {
        collection: key
      },
      meta: {
        isAuthenticated: true,
        requiresOrganisation: collection.getRequiresOrganisation(),
        requiresSuperAdmin: collection.getRequiresSuperAdmin(),
        keepAlive: true
      }
    },
    {
      path: collection.getCreateUrl(),
      name: `create-${key}`,
      component: collection.getCreatePageComponent() ||  CollectionCreatePage,
      props: {
        collection: key
      },
      meta: {
        isAuthenticated: true,
        requiresOrganisation: collection.getRequiresOrganisation(),
        requiresSuperAdmin: collection.getRequiresSuperAdmin()
      }
    },
    {
      path: `${collection.getEditUrlPrefix()}/:id`,
      name: `edit-${key}`,
      component: collection.getEditPageComponent() || CollectionEditPage,
      props: (route) => ({
        collection: key,
        id: route.params.id
      }),
      meta: {
        isAuthenticated: true,
        requiresOrganisation: collection.getRequiresOrganisation(),
        requiresSuperAdmin: collection.getRequiresSuperAdmin()
      }
    }
  ]
}).filter(routes => routes);

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: HomePage,
      meta: {
        isAuthenticated: true
      }
    },
    {
      path: '/login',
      name: 'login',
      component: LoginPage,
      props: (route) => ({
        redirect: route.query.redirect
      }),
      meta: {
        isAuthenticated: false
      }
    },
    {
      path: '/content-library',
      name: 'content-library',
      component: ContentLibraryPage,
      meta: {
        isAuthenticated: false,
        keepAlive: true
      }
    },
    {
      path: '/import',
      name: 'import',
      component: ImportPage,
      meta: {
        isAuthenticated: true,
        requiresSuperAdmin: true
      }
    },




    {
      path: '/report/historical-portfolio',
      name: 'report-historical-portfolio',
      component: HistoricalPortfolioReportPage,
      meta: {
        isAuthenticated: true,
        keepAlive: true
      }
    },
    {
      path: '/report/annual-portfolio',
      name: 'report-annual-portfolio',
      component: AnnualPortfolioReportPage,
      meta: {
        isAuthenticated: true,
        keepAlive: true
      }
    },
    {
      path: '/report/historical-asset',
      name: 'report-historical-asset',
      component: HistoricalAssetReportPage,
      meta: {
        isAuthenticated: true,
        keepAlive: true
      }
    },
    {
      path: '/report/annual-asset',
      name: 'report-annual-asset',
      component: AnnualAssetReportPage,
      meta: {
        isAuthenticated: true,
        keepAlive: true
      }
    },
    {
      path: '/report/historical-asset-manager',
      name: 'report-historical-asset-manager',
      component: HistoricalAssetManagerReportPage,
      meta: {
        isAuthenticated: true,
        keepAlive: true
      }
    },
    {
      path: '/report/annual-asset-manager',
      name: 'report-annual-asset-manager',
      component: AnnualAssetManagerReportPage,
      meta: {
        isAuthenticated: true,
        keepAlive: true
      }
    },
    {
      path: '/report/platform-growth',
      name: 'report-platform-growth',
      component: PlatformGrowthReportPage,
      meta: {
        isAuthenticated: true,
        requiresSuperAdmin: true,
        keepAlive: true
      }
    },
    {
      path: '/report/organisations-users',
      name: 'report-organisations-users',
      component: OrganisationsUsersReportPage,
      meta: {
        isAuthenticated: true,
        requiresSuperAdmin: true,
        keepAlive: true
      }
    },




    {
      path: '/export/historical-portfolio',
      name: 'export-historical-portfolio',
      component: HistoricalPortfolioExportPage,
      meta: {
        isAuthenticated: true
      }
    },
    {
      path: '/export/annual-portfolio',
      name: 'export-annual-portfolio',
      component: AnnualPortfolioExportPage,
      meta: {
        isAuthenticated: true
      }
    },
    {
      path: '/export/historical-asset',
      name: 'export-historical-asset',
      component: HistoricalAssetExportPage,
      meta: {
        isAuthenticated: true
      }
    },
    {
      path: '/export/annual-asset',
      name: 'export-annual-asset',
      component: AnnualAssetExportPage,
      meta: {
        isAuthenticated: true
      }
    },
    {
      path: '/export/historical-asset-manager',
      name: 'export-historical-asset-manager',
      component: HistoricalAssetManagerExportPage,
      meta: {
        isAuthenticated: true
      }
    },
    {
      path: '/export/annual-asset-manager',
      name: 'export-annual-asset-manager',
      component: AnnualAssetManagerExportPage,
      meta: {
        isAuthenticated: true
      }
    },



    {
      path: '/assessment/:id/interview',
      name: `edit-assessment-interview`,
      component: AssessmentInterviewPage,
      props: (route) => ({
        id: route.params.id
      }),
      meta: {
        isAuthenticated: true,
        requiresOrganisation: true
      }
    },
    {
      path: '/export/assessment-template/:id',
      name: `export-assessment-template`,
      component: AssessmentTemplateExportPage,
      props: (route) => ({
        id: route.params.id
      }),
      meta: {
        isAuthenticated: true,
        requiresOrganisation: true
      }
    },
    ...collectionRoutes,
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: NotFoundPage,
      meta: {
        isAuthenticated: false
      }
    }
    // { path: '/:pathMatch(.*)*', redirect: '/' }
  ]
});

router.beforeEach(async (to, from) => {
  await hydrateUser();
  await hydrateOrganisation(to.query.organisationId || null);

  if (to.meta.isAuthenticated && !isAuthenticated()) {
    return {name: 'login', query: {redirect: to.fullPath}}
  }

  if (to.meta.requiresOrganisation && !hasOrganisation()) {
    return {name: 'home'}
  }

  if (to.meta.requiresSuperAdmin && !isSuperAdmin()) {
    return {name: 'home'}
  }
});

export default router;

export const pushRoute = (args) => {
  const query = router.currentRoute.value.query;

  if (query.organisationId) {
    args.query = {
      ...args.query || {},
      organisationId: query.organisationId
    }
  }

  router.push(args);
}

export const replaceRoute = (args) => {
  const query = router.currentRoute.value.query;

  if (query.organisationId) {
    args.query = {
      ...args.query || {},
      organisationId: query.organisationId
    }
  }

  router.replace(args);
}

export const appendStateToRoute = (state) => {
  const url = new URL(window.location.href);

  if (state === null || state === undefined) {
    url.searchParams.delete('state');
  } else {
    url.searchParams.set('state', JSON.stringify(state));
  }

  window.history.replaceState({}, '', url);
}

export const getStateFromRoute = () => {
  const state = router.currentRoute.value.query.state || null;

  if (!state) {
    return null;
  }

  return JSON.parse(state);
}
